import React, { useCallback, useMemo, useState } from 'react';
import _ from 'underscore';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { useAdsStudioServices } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { LayoutSizeScale, useLayoutResizeObserver } from '@bingads-webui-fluent-component/layout-size';
import { AdStudioNavigation } from './ad-studio-navigation';
import { AdStudioSideNavigation } from './ad-studio-side-navigation';

const isSubPageSelected = (pathname, subPage, rootRoute) => pathname.includes(`${rootRoute}/`) && pathname.match(subPage);
const subPagesWithoutNavigation = [
  /generateimage/,
  /images\/\d+\/edit/,
  /displaytemplates\/create/,
  /displaytemplates\/\d+\/edit/,
  /videotemplates/,
  /video\/create/,
  /video\/\d+\/edit/,
];

const useStyles = makeStyles({
  hideNavigation: {
    '& .ad-studio-navigation, .ad-studio-navigation-placeholder ': {
      display: 'none',
    },
  },
  height100: {
    height: '100%',
  },
  leftMargin: {
    marginLeft: '266px',
  },
});

const getModeByLayoutSize = (layoutSize) => {
  const result = LayoutSizeScale[layoutSize] >= LayoutSizeScale.XL;

  return result;
};

export const withAdStudioNavigation = (Component) => {
  const Wrapper = (props) => {
    const {
      history, i18n, ROOT_ROUTE, logger, permissions, adsStudioAccountPreferences,
    } = props;

    const [targetDropFolder, setTargetDropFolder] = useState(null);
    const [layoutSize, setLayoutSize] = React.useState('XL');

    const dataService = useAdsStudioServices();
    useLayoutResizeObserver(setLayoutSize);
    const largeSize = useMemo(() => getModeByLayoutSize(layoutSize), [layoutSize]);

    const shouldNotShowNavigation = () => {
      if (!largeSize) {
        return true;
      }
      const { location } = history;
      const { pathname } = location;
      return subPagesWithoutNavigation.some(subPage => isSubPageSelected(pathname, subPage, ROOT_ROUTE));
    };

    const onDropToFolder = useCallback((folder) => {
      setTargetDropFolder({
        newTargetFolder: {}, // force to update targetDropFolder
        folder,
      });
    }, []);

    const resetTargetDropFolder = useCallback(() => {
      setTargetDropFolder(null);
    }, []);

    const classes = useStyles();

    return (
      <div className={shouldNotShowNavigation() ? mergeClasses('ads-studio-container', classes.hideNavigation, classes.height100) : 'ads-studio-container'}>
        {_.get(permissions, ['dynamic', 'ASNavigationSwitch']) ? (
          <AdStudioSideNavigation history={history} i18n={i18n} rootRoute={ROOT_ROUTE} dataService={dataService} logger={logger} permissions={permissions} onDropToFolder={onDropToFolder} adsStudioAccountPreferences={adsStudioAccountPreferences} />
        ) : (
          <AdStudioNavigation history={history} i18n={i18n} rootRoute={ROOT_ROUTE} dataService={dataService} logger={logger} permissions={permissions} onDropToFolder={onDropToFolder} adsStudioAccountPreferences={adsStudioAccountPreferences} />
        )}
        <div className={shouldNotShowNavigation() ? classes.height100 : classes.leftMargin}>
          <Component {...props} targetDropFolder={targetDropFolder} resetTargetDropFolder={resetTargetDropFolder} />
        </div>
      </div>
    );
  };

  Wrapper.displayName = `withAdStudioNavigation(${getDisplayName(Component)})`;
  hoistNonReactStatics(Wrapper, Component);

  return Wrapper;
};
